import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaUserShield, FaSave, FaTimes, FaSpinner } from 'react-icons/fa';
import { usePageTitle } from '../hooks/usePageTitle';
import { useHiddenFields } from '../hooks/useHiddenFields';
import { roleService, Role, Permission } from '../services/roleService';
import '../css/pages/AddRole.css';

const EditRole: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const { isHiddenModeActive } = useHiddenFields();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  
  const [role, setRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      loadRoleData();
    }
  }, [id]); // Remove dependency on isHiddenModeActive

  const loadRoleData = async () => {
    try {
      setLoading(true);
      const [roleData, permissionsData] = await Promise.all([
        roleService.getRoleById(parseInt(id!)),
        roleService.getPermissions()
      ]);

      setRole(roleData);
      setFormData({
        name: roleData.name,
        description: roleData.description || ''
      });
      
      // Show ALL permissions when editing roles, regardless of hidden mode
      setPermissions(permissionsData.permissions);
      
      // Set selected permissions from role data
      const selectedPermissionIds = roleData.rolePermissions.map(rp => rp.permission.id);
      setSelectedPermissions(selectedPermissionIds);
    } catch (error) {
      console.error('Error loading role data:', error);
      setErrors({ submit: 'Failed to load role data' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePermissionToggle = (permissionId: number) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSelectAllInModule = (module: string, modulePermissions: Permission[]) => {
    const modulePermissionIds = modulePermissions.map(p => p.id);
    const allSelected = modulePermissionIds.every(id => selectedPermissions.includes(id));
    
    if (allSelected) {
      // Deselect all in module
      setSelectedPermissions(prev => prev.filter(id => !modulePermissionIds.includes(id)));
    } else {
      // Select all in module
      setSelectedPermissions(prev => {
        const newSelected = [...prev];
        modulePermissionIds.forEach(id => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Role name must be at least 2 characters';
    }

    if (selectedPermissions.length === 0) {
      newErrors.permissions = 'At least one permission must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      await roleService.updateRole(parseInt(id!), {
        name: formData.name,
        description: formData.description,
        permissionIds: selectedPermissions
      });

      navigate('/auth/roles');
    } catch (error) {
      console.error('Error updating role:', error);
      setErrors({ submit: 'Failed to update role. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/auth/roles');
  };

  const groupedPermissions = permissions.reduce((groups, permission) => {
    const module = permission.module;
    if (!groups[module]) {
      groups[module] = [];
    }
    groups[module].push(permission);
    return groups;
  }, {} as Record<string, Permission[]>);

  if (loading) {
    return (
      <div className="loading-container">
        <FaSpinner className="loading-spinner" />
        <p>Loading role data...</p>
      </div>
    );
  }

  if (!role) {
    return (
      <div className="error-container">
        <p>Role not found</p>
        <button onClick={handleCancel} className="btn-secondary">
          Back to Roles
        </button>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div style={{ color: '#1a1a1a' }}>
          <h1 style={{ color: '#1a1a1a', margin: '0 0 4px 0' }}><FaUserShield className="page-icon" style={{ marginRight: '8px' }} /> Edit Role</h1>
        </div>
        <div className="header-actions">
          <button type="button" className="btn-secondary" onClick={handleCancel}>
            <FaTimes />
            Cancel
          </button>
          <button 
            type="submit" 
            form="edit-role-form"
            className="btn-primary"
            disabled={saving}
          >
            <FaSave />
            {saving ? 'Updating...' : 'Update Role'}
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="add-role-content">
        <form id="edit-role-form" onSubmit={handleSubmit} className="role-form">
          {/* Basic Information */}
          <div className="form-section">
            <h2>Role Information</h2>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="name">Role Name *</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter role name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-field">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter role description (optional)"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Permissions Section */}
          <div className="form-section">
            <div className="section-header">
              <h2>Permissions</h2>
              <div className="permission-summary">
                Selected: {selectedPermissions.length} / {permissions.length}
              </div>
            </div>

            {errors.permissions && (
              <div className="error-message section-error">{errors.permissions}</div>
            )}

            <div className="permissions-container">
              {Object.entries(groupedPermissions).map(([module, modulePermissions]) => {
                const modulePermissionIds = modulePermissions.map(p => p.id);
                const selectedInModule = modulePermissionIds.filter(id => selectedPermissions.includes(id)).length;
                const allSelected = selectedInModule === modulePermissionIds.length;
                const someSelected = selectedInModule > 0 && selectedInModule < modulePermissionIds.length;

                return (
                  <div key={module} className="permission-module">
                    <div className="module-header">
                      <label className="module-checkbox">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={input => {
                            if (input) input.indeterminate = someSelected;
                          }}
                          onChange={() => handleSelectAllInModule(module, modulePermissions)}
                        />
                        <span className="module-name">
                          {module.charAt(0).toUpperCase() + module.slice(1)} Module
                        </span>
                        <span className="module-count">
                          ({selectedInModule}/{modulePermissionIds.length})
                        </span>
                      </label>
                    </div>

                    <div className="permission-list">
                      {modulePermissions.map(permission => (
                        <label key={permission.id} className="permission-item">
                          <input
                            type="checkbox"
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={() => handlePermissionToggle(permission.id)}
                          />
                          <div className="permission-details">
                            <span className="permission-name">{permission.name}</span>
                            <span className="permission-description">{permission.description}</span>
                          </div>
                          <span className={`action-badge action-${permission.action}`}>
                            {permission.action}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditRole;
